import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, HelpCircle, MapPin } from "lucide-react";
import { SERVICE_CATEGORIES } from "../data/content";
import { SERVICE_LONG_CONTENT } from "../data/serviceContent";
import { SERVICE_FAQS } from "../data/seoContent";
import { CITY_SERVICE_BUILDERS, CITY_SERVICE_CITIES } from "../data/cityServiceContent";
import { getCityServiceArticle, getCityServiceFaqs } from "../data/cityServiceArticles";
import SEO, { breadcrumbSchema, serviceSchema, faqSchema } from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import ContactForm from "../components/ContactForm";

/**
 * City × service landing page — e.g. /services/asbestos/london.
 *
 * Reuses the source-of-truth long-form content from serviceContent.js so we
 * don't fork the technical copy 25 times. Only the H1, meta, intro paragraph
 * and schema location are city-specific. This is "programmatic SEO" done
 * cleanly: one route, one render path, multiple geo-targeted URLs in the
 * sitemap.
 */
export default function CityServiceDetail() {
  const { slug, city: citySlug } = useParams();
  const service = SERVICE_CATEGORIES.find((s) => s.slug === slug);
  const city = CITY_SERVICE_CITIES.find((c) => c.slug === citySlug);
  const builder = CITY_SERVICE_BUILDERS[slug];

  if (!service || !city || !builder) {
    return <Navigate to={service ? `/services/${slug}` : "/services"} replace />;
  }

  const seo = builder(city);
  const longContent = SERVICE_LONG_CONTENT[slug];
  const cityArticle = getCityServiceArticle(slug, citySlug);
  const cityFaqs = getCityServiceFaqs(slug, citySlug);
  const baseFaqs = SERVICE_FAQS[slug] || [];
  // Localized FAQs first (city-specific signal), then general FAQs
  const faqs = [...cityFaqs, ...baseFaqs];
  const otherCities = CITY_SERVICE_CITIES.filter((c) => c.slug !== citySlug);
  const path = `/services/${slug}/${city.slug}`;

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: service.name, path: `/services/${slug}` },
    { name: city.name, path },
  ];

  const schemas = [
    breadcrumbSchema(breadcrumbs),
    serviceSchema({
      name: seo.h1,
      description: seo.description,
      path,
      areaServed: `${city.name}, ${city.region}, United Kingdom`,
      serviceType: service.name,
      offers: (service.items || []).map((i) => i.name),
    }),
  ];
  if (faqs.length) schemas.push(faqSchema(faqs));

  return (
    <div data-testid={`page-city-service-${slug}-${citySlug}`}>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={path}
        schema={schemas}
      />

      <section className="bg-[#0A0F1C] text-white pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-10">
            <Breadcrumb items={breadcrumbs} theme="dark" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#7DD3C7] mb-6">
                <MapPin className="w-3 h-3 inline mr-1.5 mb-0.5" /> {city.region}
              </p>
              <h1 className="font-display text-4xl lg:text-6xl font-bold tracking-tight leading-[1.05]">{seo.h1}</h1>
              <p className="mt-8 text-lg text-white/70 max-w-3xl leading-relaxed">{seo.intro}</p>
            </div>
            <div className="lg:col-span-4 border-l-2 border-[#3FBAB0] pl-6">
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">Applicable legislation</p>
              <p className="font-display text-lg text-white font-semibold leading-snug">{service.legislation}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-4">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Scope in {city.name}</p>
              <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">What we deliver across {city.name} and {city.region}.</h2>
              <Link to={`/services/${slug}`} className="mt-6 inline-flex items-center gap-2 text-[#1FA59A] font-semibold text-sm hover:underline" data-testid={`back-to-service-${slug}`}>
                Full {service.name} service overview <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                {service.items.map((it, i) => (
                  <div key={i} className="bg-white p-6 min-h-[150px]">
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#3FBAB0] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                      <div>
                        <p className="font-display text-lg font-semibold text-slate-900 mb-2">{it.name}</p>
                        <p className="text-slate-600 text-sm leading-relaxed">{it.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {cityArticle && (
        <section className="bg-white py-20 lg:py-28 border-t border-slate-100" data-testid="city-service-article">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#1FA59A] font-semibold mb-4">Local context · {city.name}</p>
                <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight">{service.name} in {city.name}: the local reality.</h2>
                <p className="mt-6 text-slate-600 text-sm leading-relaxed">Building stock, sector exposure and recurring findings — written by the surveyors who deliver across {city.region}.</p>
                <div className="mt-8 border-t border-slate-200 pt-6">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Also covering {service.name.toLowerCase()} in</p>
                  <ul className="space-y-2">
                    {otherCities.map((c) => (
                      <li key={c.slug}>
                        <Link
                          to={`/services/${slug}/${c.slug}`}
                          className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-[#1FA59A] font-medium"
                          data-testid={`sibling-city-${c.slug}`}
                        >
                          <MapPin className="w-3.5 h-3.5 text-[#3FBAB0]" /> {c.name}
                          <ArrowUpRight className="w-3 h-3 opacity-60" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
            <article className="lg:col-span-8">
              <div className="prose prose-slate max-w-none
                prose-headings:font-display prose-headings:tracking-tight prose-headings:text-slate-900
                prose-h3:text-2xl prose-h3:font-semibold prose-h3:mt-12 prose-h3:mb-4 first:prose-h3:mt-0
                prose-p:text-slate-700 prose-p:leading-[1.75] prose-p:text-base lg:prose-p:text-[17px]
                prose-strong:text-slate-900 prose-strong:font-semibold
                [&_section]:mb-2"
                dangerouslySetInnerHTML={{ __html: cityArticle }} />
            </article>
          </div>
        </section>
      )}

      {longContent && (
        <section className="bg-[#F8FAFC] border-y border-slate-200 py-20 lg:py-28" data-testid="service-long-content">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Technical briefing</p>
                <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight">The full picture on {service.name.toLowerCase()} in {city.name}.</h2>
                <div className="mt-8 border-t border-slate-300 pt-6">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Core legislation &amp; guidance</p>
                  <p className="font-display text-base text-slate-900 font-semibold leading-snug">{service.legislation}</p>
                </div>
              </div>
            </aside>
            <article className="lg:col-span-8">
              <div className="prose prose-slate max-w-none
                prose-headings:font-display prose-headings:tracking-tight prose-headings:text-slate-900
                prose-h3:text-2xl prose-h3:font-semibold prose-h3:mt-12 prose-h3:mb-4 first:prose-h3:mt-0
                prose-p:text-slate-700 prose-p:leading-[1.75] prose-p:text-base lg:prose-p:text-[17px]
                prose-strong:text-slate-900 prose-strong:font-semibold
                prose-ul:text-slate-700 prose-li:my-1
                [&_section]:mb-2"
                dangerouslySetInnerHTML={{ __html: longContent }} />
            </article>
          </div>
        </section>
      )}

      {faqs.length > 0 && (
        <section className="bg-white py-20 lg:py-28 border-b border-slate-200" data-testid="service-faq">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Frequently asked · {city.name}</p>
              <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight">Answers for duty holders in {city.name}.</h2>
            </div>
            <div className="lg:col-span-8 divide-y divide-slate-200 border-y border-slate-200">
              {faqs.map((f, i) => (
                <details key={i} className="group py-6" data-testid={`faq-${i}`}>
                  <summary className="flex items-start gap-4 cursor-pointer list-none">
                    <HelpCircle className="w-5 h-5 text-[#1FA59A] mt-1 flex-shrink-0" strokeWidth={1.75} />
                    <span className="font-display text-lg lg:text-xl font-semibold text-slate-900 flex-1">{f.q}</span>
                    <span className="text-slate-400 group-open:rotate-45 transition-transform text-2xl leading-none select-none">+</span>
                  </summary>
                  <div className="mt-4 pl-9 text-slate-700 leading-[1.75] text-base" dangerouslySetInnerHTML={{ __html: f.a }} />
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#F8FAFC] py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#1FA59A] font-semibold mb-4">Free no-obligation quote · 24-hour response</p>
            <h2 className="font-display text-4xl font-bold text-slate-900 tracking-tight">{seo.h1.replace(' in ', ' — ')}. Scoped today.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Share a few details and an OEC expert will respond with a fixed-fee proposal within one working day. No call centres, no chasing — direct line to our {city.name} surveying team.</p>
          </div>
          <div className="lg:col-span-7">
            <ContactForm defaultService={service.name} />
          </div>
        </div>
      </section>
    </div>
  );
}
